import { createSignal, createEffect, For } from "solid-js";
import "./guestbook.scss";
import { useStateContext } from "../context";
import { Show } from "solid-js/web";
import { clickOutside } from "../directives";

type GuestBookEntry = {
    record_id: string;
    name: string;
    message: string;
    entry_date: string;
};

function formatDate(dateString: string) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
}

const Guestbook = () => {
    // State to hold guestbook entries
    const [entries, setEntries] = createSignal([]);
    const [loading, setLoading] = createSignal(true);
    const [error, setError] = createSignal<string | null>(null);
    const { isGuestbookOpen, toggleGuestbook } = useStateContext();

    // Fetch guestbook data
    const fetchGuestbook = async () => {
        try {
            setLoading(true)
            const response = await fetch("/api/openguestbook");
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setEntries(data.entries);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    };

    async function addEntry(e: Event) {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const data = Object.fromEntries(formData.entries());
        try {
            let resp = await fetch("/api/addguestbookentry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });
            if (resp.ok) {
                (e.target as HTMLFormElement).reset();
                fetchGuestbook();
            } else {
                throw new Error("Failed to add entry");
            }
        } catch (err) {
            setError((err as Error).message);
        }
    };

    // Fetch data when the component mounts
    createEffect(() => {
        fetchGuestbook();
    });

    return (
        <Show when={isGuestbookOpen()}>
            <div use:clickOutside={toggleGuestbook()} class="guestbook">
                <h2>Guestbook</h2>
                {error() && <p class="guestbook-error">Error: {error()}</p>}
                <Show when={loading()}>
                    <p class="guestbook-loading">Loading Guestbook Entries...</p>
                </Show>
                <ul class="guestbook-entries">
                    <For each={entries().reverse()}>
                        {(entry: GuestBookEntry) => {
                            return (
                                <li class="guestbook-entry">
                                    <strong class="guestbook-name">{entry.name}</strong> <br /> <small class="guestbook-message">{entry.message}</small> <br /> <small class="guestbook-date">on {formatDate(entry.entry_date)}</small>
                                </li>
                            );
                        }}
                    </For>
                </ul>
                <form class="guestbook-form" onSubmit={addEntry}>
                    <input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        required
                        class="guestbook-form-name"
                    />
                    <textarea
                        placeholder="Leave a message..."
                        name="message"
                        required
                        class="guestbook-form-message"
                    />
                    <button type="submit" class="guestbook-button">Add Entry</button>
                </form>
            </div>
        </Show>
    );
};

export default Guestbook;

